import { useMutation } from '@apollo/client/react'
import { Badge, Button, FailureNotice, Notice, Section, useTranslate } from '@athena/ui'
import { useRef, useState } from 'react'

import {
  RestoreStudentConnectionDocument,
  RestoreSuspendedPracticeDocument,
  RevokeInvitationDocument,
  type SubscriptionFieldsFragment,
} from '../../graphql/generated/operations.js'
import { useFormat } from '../../lib/format.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

import { SubscriptionResourceList, SubscriptionResourceRow } from './SubscriptionResourceList.js'

/** One change, resolved to the server's refusals, or `null` when no answer came back with it. */
type ResourceCall = () => Promise<
  readonly { code: string; message: string; params: readonly string[] }[] | null
>

export interface SuspendedResourcesProps {
  subscription: SubscriptionFieldsFragment
}

/**
 * What a plan is not holding, and how to get it back.
 *
 * Three lists, and they are the other half of the promise the downgrade
 * screen makes: nothing was deleted, so everything here is still there and
 * still restorable. The copy says so on each list rather than once at the top,
 * because a tutor scrolling to "practices set aside" needs to read it there.
 *
 * Restoring needs a slot, and the server is what decides: pressing the button
 * with no room comes back with the limit, the count, and what to do about it.
 * The button is not disabled on a guess about capacity — the count could be a
 * moment stale, and a control that is enabled and honest beats one that is
 * disabled and wrong.
 *
 * The suspended lists also show *why*: `SUBSCRIPTION_LIMIT` is the product and
 * `TUTOR_REQUEST` is the tutor. Restoring after an upgrade is theirs to do in
 * both cases, which is exactly why the two are kept apart — an automatic
 * reactivation would quietly undo a decision they made.
 */
export function SuspendedResources({ subscription }: SuspendedResourcesProps) {
  const t = useTranslate()
  const format = useFormat()
  const userErrorText = useFirstUserErrorText()
  const [error, setError] = useState<string | null>(null)
  /** A change that never got an answer, and the call to send again. */
  const [failure, setFailure] = useState<unknown>()
  const lastCall = useRef<ResourceCall | null>(null)

  /**
   * Refetched by operation name rather than by document.
   *
   * Restoring a student or a practice changes what the other two screens show,
   * so all three are re-read. Names rather than documents because a name
   * re-runs whatever variables the active query already has — the practice
   * list is filtered by subject on the shelf, and refetching it with a
   * document would re-run it unfiltered and quietly widen what the tutor was
   * looking at.
   */
  const refetch = { refetchQueries: ['MySubscription', 'MyStudents', 'MyPractices'] }
  const [restoreStudent, restoringStudent] = useMutation(RestoreStudentConnectionDocument, refetch)
  const [restorePractice, restoringPractice] = useMutation(
    RestoreSuspendedPracticeDocument,
    refetch,
  )
  const [revoke, revoking] = useMutation(RevokeInvitationDocument, refetch)

  const nothingToShow =
    subscription.suspendedStudents.length === 0 &&
    subscription.suspendedPractices.length === 0 &&
    subscription.pendingInvitations.length === 0

  if (nothingToShow) return null

  return (
    <div className="flex flex-col gap-6">
      {error ? <Notice tone="error" title={error} /> : null}
      {failure === undefined ? null : (
        <FailureNotice
          title={t('subscription.changeNotMade')}
          error={failure}
          retrying={restoringStudent.loading || restoringPractice.loading || revoking.loading}
          onRetry={() => {
            if (lastCall.current) void run(lastCall.current)
          }}
        />
      )}

      {subscription.suspendedStudents.length > 0 ? (
        <Section id="students-set-aside" title={t('subscription.suspendedStudentsHeading')}>
          <p className="text-body text-text-secondary">{t('subscription.suspendedStudentsBody')}</p>
          <SubscriptionResourceList>
            {subscription.suspendedStudents.map((student) => (
              <SubscriptionResourceRow
                key={student.studentId}
                name={student.displayName}
                reason={
                  student.reason === 'TUTOR_REQUEST'
                    ? t('subscription.suspendedByTutor')
                    : t('subscription.suspendedBySubscription')
                }
                state={
                  <Badge status="setAside" className="px-3 py-1.5">
                    {t('subscription.suspendedState')}
                  </Badge>
                }
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={restoringStudent.loading}
                    onClick={() => {
                      void run(() =>
                        restoreStudent({ variables: { studentId: student.studentId } }).then(
                          (result) => result.data?.restoreStudentConnection.userErrors ?? null,
                        ),
                      )
                    }}
                  >
                    {t('subscription.restore')}
                  </Button>
                }
              />
            ))}
          </SubscriptionResourceList>
        </Section>
      ) : null}

      {subscription.suspendedPractices.length > 0 ? (
        <Section id="practices-set-aside" title={t('subscription.suspendedPracticesHeading')}>
          <p className="text-body text-text-secondary">
            {t('subscription.suspendedPracticesBody')}
          </p>
          <SubscriptionResourceList>
            {subscription.suspendedPractices.map((practice) => (
              <SubscriptionResourceRow
                key={practice.practiceId}
                name={practice.title}
                reason={t('subscription.suspendedBySubscription')}
                state={
                  <Badge status="setAside" className="px-3 py-1.5">
                    {t('subscription.suspendedState')}
                  </Badge>
                }
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={restoringPractice.loading}
                    onClick={() => {
                      void run(() =>
                        restorePractice({ variables: { practiceId: practice.practiceId } }).then(
                          (result) => result.data?.restoreSuspendedPractice.userErrors ?? null,
                        ),
                      )
                    }}
                  >
                    {t('subscription.restore')}
                  </Button>
                }
              />
            ))}
          </SubscriptionResourceList>
        </Section>
      ) : null}

      {subscription.pendingInvitations.length > 0 ? (
        <Section id="invitations-holding" title={t('subscription.invitationsHeading')}>
          <p className="text-body text-text-secondary">{t('subscription.invitationsBody')}</p>
          <SubscriptionResourceList>
            {subscription.pendingInvitations.map((invitation) => (
              <SubscriptionResourceRow
                key={invitation.id}
                name={invitation.studentName || t('student.invite.pendingConnection')}
                reason={t('subscription.invitationExpires', [format.date(invitation.expiresAt)])}
                state={
                  <Badge status="inProgress" className="px-3 py-1.5">
                    {t('subscription.invitationState')}
                  </Badge>
                }
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={revoking.loading}
                    onClick={() => {
                      void run(() =>
                        revoke({ variables: { invitationId: invitation.id } }).then(
                          (result) => result.data?.revokeInvitation.userErrors ?? null,
                        ),
                      )
                    }}
                  >
                    {t('subscription.invitationRevoke')}
                  </Button>
                }
              />
            ))}
          </SubscriptionResourceList>
        </Section>
      ) : null}
    </div>
  )

  async function run(call: ResourceCall) {
    setError(null)
    lastCall.current = call
    try {
      const errors = await call()
      setFailure(undefined)
      // No payload at all is not a success: nothing says the change was made.
      if (errors === null) setFailure(new Error('The change came back without a result.'))
      else if (errors.length > 0) setError(userErrorText(errors, t('common.tryAgain')))
    } catch (caught) {
      // A rejected request. Said here, with a retry of the same change —
      // restoring what is restored, or revoking what is revoked, is refused
      // in words rather than done twice.
      setFailure(caught)
    }
  }
}
